import React, { useState } from 'react';
import * as LucideIcons from 'lucide-react';

interface IconAvatarProps {
  iconName: string;
  avatarUrl?: string;
  size?: number;
  gradient?: string;
}

export const IconAvatar: React.FC<IconAvatarProps> = ({
  iconName,
  avatarUrl,
  size = 40,
  gradient = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const IconComponent = (LucideIcons as any)[iconName];
  
  if (!IconComponent) return null;

  const shouldShowImage = avatarUrl && !imageError;

  // Reset image state when avatarUrl changes
  React.useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [avatarUrl]);

  // Set a timeout to fallback to icon if image doesn't load within 5 seconds
  React.useEffect(() => {
    if (!avatarUrl || imageError || imageLoaded) return;
    
    const timeout = setTimeout(() => {
      if (!imageLoaded) {
        console.warn(`[IconAvatar] Avatar image failed to load within 5 seconds:`, avatarUrl);
        setImageError(true);
      }
    }, 5000);
    
    return () => clearTimeout(timeout);
  }, [avatarUrl, imageError, imageLoaded]);

  return (
    <div
      className="flex items-center justify-center text-white flex-shrink-0 overflow-hidden relative"
      style={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        borderRadius: '15%',
        background: shouldShowImage ? 'transparent' : gradient,
      }}
    >
      {shouldShowImage ? (
        <>
          {/* Skeleton loader - shown while image is loading */}
          {!imageLoaded && (
            <div 
              className="absolute inset-0 bg-gray-200 animate-pulse"
              style={{
                borderRadius: '15%',
              }}
            />
          )}
          
          {/* Actual image - fades in when loaded */}
          <img
            src={avatarUrl}
            alt=""
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        </>
      ) : (
        <IconComponent className="flex-shrink-0" size={size / 2} width={size / 2} height={size / 2} />
      )}
    </div>
  );
};
